/**
 * FHIR-level helpers for the appointment page (participant extraction, encounter lookup, linked appointments).
 */
import type {
  Appointment,
  Encounter,
  HumanName,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  RelatedPerson,
} from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';
import type { useMedplum } from '@medplum/react-hooks';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import type { LinkedAppointmentInfo } from '../types';
import { formatLinkedDate, formatLinkedTime } from './dateFormatters';

const TEMPLATE_IDENTIFIER_SYSTEM = 'https://unify.health/fhir/questionnaire-template';

const MEDPLUM_SERVER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function looksLikeMedplumServerId(id: string): boolean {
  return MEDPLUM_SERVER_ID_RE.test(id);
}

const FHIR_REFERENCE_RE = /^[A-Z][A-Za-z]+\/(.+)$/;

/** True when `display` is a human label, not a raw resource id or reference copied into actor.display. */
export function isHumanParticipantDisplay(
  display: string | undefined,
  resourceId?: string,
): display is string {
  const trimmed = display?.trim();
  if (!trimmed) return false;
  // Writers sometimes fall back to the reference itself (`Practitioner/<id>`) — not a name.
  const withoutResourceType = FHIR_REFERENCE_RE.exec(trimmed)?.[1] ?? trimmed;
  if (looksLikeMedplumServerId(withoutResourceType)) return false;
  if (resourceId && withoutResourceType === resourceId) return false;
  return true;
}

/** Avoid showing raw Medplum ids when Appointment.participant lacks actor.display. */
export function patientDisplayLabelFromReference(
  reference: string | undefined,
  explicitDisplay?: string,
): string {
  const id = reference?.includes('/')
    ? reference.slice(reference.indexOf('/') + 1).trim()
    : reference?.trim() ?? '';
  if (isHumanParticipantDisplay(explicitDisplay, id)) return explicitDisplay.trim();
  if (!id || looksLikeMedplumServerId(id)) return 'Client';
  return id;
}

/** Resolve the primary provider label for an appointment header. */
export function practitionerDisplayLabelFromAppointment(
  appointment: Appointment,
  practitionerResource?: Practitioner,
): string | undefined {
  const participant = appointment.participant?.find((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  const practitionerId = participant?.actor?.reference?.replace(/^Practitioner\//, '').trim() ?? '';
  const explicitDisplay = participant?.actor?.display;

  if (practitionerResource) {
    const staff = formatPractitionerStaffLabel(practitionerResource);
    if (staff) return staff;
  }

  if (isHumanParticipantDisplay(explicitDisplay, practitionerId)) {
    return explicitDisplay.trim();
  }

  return undefined;
}

export function formatFhirHumanName(name: HumanName | undefined): string | undefined {
  if (!name) return undefined;
  if (name.text?.trim()) return name.text.trim();
  const given = name.given?.join(' ') ?? '';
  const family = name.family ?? '';
  const combined = `${given} ${family}`.trim();
  return combined || undefined;
}

export function formatFhirPatientDisplayName(
  patient: { name?: HumanName[] } | undefined,
): string | undefined {
  if (!patient) return undefined;
  const preferred = patient.name?.find((n) => n.use === 'usual');
  return formatFhirHumanName(preferred) ?? formatFhirHumanName(patient.name?.[0]);
}

/** Minimal Patient for appointment header when compartment read fails but id is known. */
export function patientStubFromAppointmentParticipant(
  appointment: Appointment,
  patientId: string,
): Patient {
  const participant = appointment.participant?.find(
    (p) => p.actor?.reference === `Patient/${patientId}`,
  );
  const display = patientDisplayLabelFromReference(
    `Patient/${patientId}`,
    participant?.actor?.display,
  );
  const parts = display.split(/\s+/).filter(Boolean);
  if (parts.length === 0 || display === 'Client') {
    return { resourceType: 'Patient', id: patientId, name: [{ given: ['Client'] }] };
  }
  const family = parts.length > 1 ? parts.pop()! : '';
  const given = parts;
  return {
    resourceType: 'Patient',
    id: patientId,
    name: [{ given, ...(family ? { family } : {}) }],
  };
}

function questionnaireUrlCandidates(ref: string, slug?: string): string[] {
  const candidates = new Set<string>();
  if (ref.startsWith('http')) candidates.add(ref);
  if (slug) {
    candidates.add(`https://unify.health/fhir/Questionnaire/${slug}`);
    candidates.add(`https://unify.health/Questionnaire/${slug}`);
    candidates.add(`https://unify.health/fhir/Questionnaire/${slug}`);
  }
  return [...candidates];
}

async function searchQuestionnaireByUrl(
  medplum: MedplumClient | ReturnType<typeof useMedplum>,
  url: string,
): Promise<Questionnaire | null> {
  try {
    const found = await medplum.searchResources('Questionnaire', { url, _count: '5' });
    return found.find((q) => q.url === url) ?? found[0] ?? null;
  } catch {
    return null;
  }
}

async function searchQuestionnaireBySlug(
  medplum: MedplumClient | ReturnType<typeof useMedplum>,
  slug: string,
): Promise<Questionnaire | null> {
  try {
    const byName = await medplum.searchResources('Questionnaire', {
      name: slug,
      status: 'active',
      _count: '20',
    });
    const nameMatch = byName.find((q) => q.name === slug) ?? byName[0];
    if (nameMatch) return nameMatch;
  } catch {
    // fall through
  }

  try {
    const byIdentifier = await medplum.searchResources('Questionnaire', {
      identifier: `${TEMPLATE_IDENTIFIER_SYSTEM}|${slug}`,
      _count: '1',
    });
    if (byIdentifier[0]) return byIdentifier[0];
  } catch {
    // fall through
  }

  for (const url of questionnaireUrlCandidates(slug, slug)) {
    const found = await searchQuestionnaireByUrl(medplum, url);
    if (found) return found;
  }

  return null;
}

/** Display row for someone listed as having attended (from Appointment.participant). */
export interface AppointmentAttendee {
  displayName: string;
  /** In-app profile URL when the actor type has a known route (e.g. Patient, Practitioner). */
  profileHref?: string;
  /** FHIR reference e.g. Patient/abc — used for note participant attendance keys. */
  actorReference?: string;
}

/** Prefer loaded Patient.name over Appointment.participant.actor.display (often missing). */
export function enrichAppointmentAttendeeDisplayNames(
  attendees: AppointmentAttendee[],
  patientsById: Record<string, Patient | undefined>,
  relatedPersonsById: Record<string, RelatedPerson | undefined> = {},
): AppointmentAttendee[] {
  return attendees.map((a) => {
    const ref = a.actorReference?.trim();
    if (!ref) return a;
    if (ref.startsWith('Patient/')) {
      const id = ref.slice('Patient/'.length).trim();
      const fromPatient = formatFhirPatientDisplayName(patientsById[id]);
      if (fromPatient) return { ...a, displayName: fromPatient };
      return a;
    }
    if (ref.startsWith('RelatedPerson/')) {
      const id = ref.slice('RelatedPerson/'.length).trim();
      const fromRelatedPerson = formatFhirHumanName(relatedPersonsById[id]?.name?.[0]);
      if (fromRelatedPerson) return { ...a, displayName: fromRelatedPerson };
    }
    return a;
  });
}

function normalizeAppointmentStatus(status: string | undefined): string {
  return (status ?? '').toLowerCase().replace(/\s+/g, '-');
}

/**
 * Map a FHIR actor reference to an in-app profile path, when one exists.
 */
export function getProfileHrefForActorReference(reference: string | undefined): string | undefined {
  if (!reference) return undefined;
  if (reference.startsWith('Patient/')) {
    const id = reference.slice('Patient/'.length).trim();
    return id ? `/dashboard/clients/${id}` : undefined;
  }
  if (reference.startsWith('Practitioner/')) {
    const id = reference.slice('Practitioner/'.length).trim();
    if (!id || id === 'mock') return undefined;
    return `/dashboard/settings/users/${id}`;
  }
  return undefined;
}

/**
 * Participants who attended, derived from {@link Appointment.participant} and appointment status.
 * Uses FHIR participant status (excludes declined); for no-show, lists practitioners only.
 */
export function getAttendedParticipantsFromAppointment(appointment: Appointment): AppointmentAttendee[] {
  const apptStatus = normalizeAppointmentStatus(appointment.status);
  const participants = appointment.participant ?? [];

  if (apptStatus === 'cancelled' || apptStatus === 'entered-in-error') {
    return [];
  }

  const isNoShow = apptStatus === 'noshow' || apptStatus === 'no-show';
  const isAttendedSession =
    apptStatus === 'fulfilled' || apptStatus === 'arrived' || apptStatus === 'checked-in';

  if (!isAttendedSession && !isNoShow) {
    return [];
  }

  const filtered = participants.filter((p) => {
    if (p.status?.toLowerCase() === 'declined') return false;
    const ref = p.actor?.reference ?? '';
    if (isNoShow) {
      return ref.startsWith('Practitioner/');
    }
    return !!(ref || p.actor?.display?.trim());
  });

  const seen = new Set<string>();
  const out: AppointmentAttendee[] = [];

  for (const p of filtered) {
    const ref = p.actor?.reference?.trim();
    const key = ref || p.actor?.display?.trim() || '';
    if (!key || seen.has(key)) continue;
    seen.add(key);

    const displayName = patientDisplayLabelFromReference(ref, p.actor?.display);
    if (!displayName) continue;

    out.push({
      displayName,
      profileHref: getProfileHrefForActorReference(ref),
    });
  }

  return out;
}

function isClientParticipantReference(reference: string | undefined): boolean {
  return Boolean(
    reference?.startsWith('Patient/') || reference?.startsWith('RelatedPerson/'),
  );
}

/**
 * Returns ALL client-side participants (Patient + RelatedPerson) from an appointment,
 * regardless of attendance status. Couples/family sessions store partners as RelatedPerson PART.
 */
export function getAllClientParticipantsFromAppointment(appointment: Appointment): AppointmentAttendee[] {
  const participants = appointment.participant ?? [];
  const seen = new Set<string>();
  const out: AppointmentAttendee[] = [];

  for (const p of participants) {
    const ref = p.actor?.reference?.trim();
    if (!isClientParticipantReference(ref)) continue;
    if (!ref || seen.has(ref)) continue;
    seen.add(ref);

    const displayName = patientDisplayLabelFromReference(ref, p.actor?.display);
    if (!displayName) continue;

    out.push({
      displayName,
      profileHref: getProfileHrefForActorReference(ref),
      actorReference: ref,
    });
  }

  return out;
}

/**
 * Returns ALL Patient/ participants from an appointment regardless of their attendance status.
 * Used to build the full "Participants" list for couples/family sessions, where some members
 * may be marked 'declined' (no-show) but should still appear (with strikethrough) in the UI.
 */
export function getAllPatientParticipantsFromAppointment(appointment: Appointment): AppointmentAttendee[] {
  const participants = appointment.participant ?? [];
  const seen = new Set<string>();
  const out: AppointmentAttendee[] = [];

  for (const p of participants) {
    const ref = p.actor?.reference?.trim();
    if (!ref?.startsWith('Patient/')) continue;
    if (seen.has(ref)) continue;
    seen.add(ref);

    const displayName = patientDisplayLabelFromReference(ref, p.actor?.display);
    if (!displayName) continue;

    out.push({
      displayName,
      profileHref: getProfileHrefForActorReference(ref),
      actorReference: ref,
    });
  }

  return out;
}

export function getPatientIdFromAppointment(appointment: Appointment): string | undefined {
  const patientParticipant = appointment.participant?.find((p) =>
    p.actor?.reference?.startsWith('Patient/'),
  );
  return patientParticipant?.actor?.reference?.replace('Patient/', '');
}

export function getPractitionerIdFromAppointment(appointment: Appointment): string | undefined {
  const practitionerParticipant = appointment.participant?.find((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  return practitionerParticipant?.actor?.reference?.replace('Practitioner/', '');
}

/**
 * Extract Questionnaire logical id from a reference string (relative, canonical, or full URL).
 */
export function extractQuestionnaireIdFromCanonical(ref: string | undefined): string | undefined {
  if (!ref?.trim()) return undefined;
  const t = ref.trim();
  const m = t.match(/Questionnaire\/([^/?#]+)/);
  return m?.[1];
}

/**
 * Normalize `QuestionnaireResponse.questionnaire` (string canonical or Reference) to a single lookup string.
 */
export function getQuestionnaireRefString(
  questionnaire: QuestionnaireResponse['questionnaire'],
): string | undefined {
  if (questionnaire == null) return undefined;
  if (typeof questionnaire === 'string') {
    const s = questionnaire.trim();
    return s || undefined;
  }
  if (typeof questionnaire === 'object') {
    const ref = (questionnaire as { reference?: string }).reference;
    if (typeof ref === 'string' && ref.trim()) return ref.trim();
  }
  return undefined;
}

/**
 * Load Questionnaire for a QR reference — read by id, search by _id, then by canonical `url` if needed.
 */
export async function fetchQuestionnaireForResponseRef(
  medplum: MedplumClient | ReturnType<typeof useMedplum>,
  ref: string,
): Promise<Questionnaire | null> {
  const trimmed = ref.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('http')) {
    const byUrl = await searchQuestionnaireByUrl(medplum, trimmed);
    if (byUrl) return byUrl;
  }

  const id = extractQuestionnaireIdFromCanonical(trimmed);
  if (id) {
    if (looksLikeMedplumServerId(id)) {
      try {
        return await medplum.readResource('Questionnaire', id);
      } catch {
        try {
          const found = await medplum.searchResources('Questionnaire', { _id: id, _count: '1' });
          if (found[0]) return found[0];
        } catch {
          // fall through
        }
      }
    }

    const bySlug = await searchQuestionnaireBySlug(medplum, id);
    if (bySlug) return bySlug;
  }

  for (const url of questionnaireUrlCandidates(trimmed, id)) {
    const found = await searchQuestionnaireByUrl(medplum, url);
    if (found) return found;
  }

  return null;
}

/**
 * Load Questionnaire for display after save; uses search fallback when read-by-id fails (e.g. reference shape).
 */
export async function loadQuestionnaireForNote(
  medplum: MedplumClient | ReturnType<typeof useMedplum>,
  questionnaireRef: string | undefined,
): Promise<Questionnaire | null> {
  if (!questionnaireRef?.trim()) return null;
  return fetchQuestionnaireForResponseRef(medplum, questionnaireRef);
}

/** True when an Encounter.appointment entry points at this Appointment id (any reference shape). */
export function encounterReferencesAppointment(
  encounter: Encounter,
  appointmentId: string,
): boolean {
  const normalizedId = appointmentId.trim();
  if (!normalizedId) return false;
  for (const appt of encounter.appointment ?? []) {
    const ref = appt.reference?.trim();
    if (!ref) continue;
    if (ref === `Appointment/${normalizedId}` || ref.endsWith(`/${normalizedId}`)) {
      return true;
    }
  }
  return false;
}

function encounterMatchesPatient(encounter: Encounter, patientId: string | undefined): boolean {
  if (!patientId) return true;
  const subjectRef = encounter.subject?.reference?.replace(/^Patient\//, '');
  return subjectRef === patientId;
}

/**
 * Pick the Encounter linked to this appointment. When several match, prefer the one
 * whose subject matches the chart patient; otherwise use the first appointment-linked hit.
 */
export function pickEncounterForAppointment(
  encounters: Encounter[],
  appointmentId: string,
  patientId: string | undefined,
): Encounter | undefined {
  const linked = encounters.filter((enc) => encounterReferencesAppointment(enc, appointmentId));
  if (linked.length === 0) return undefined;
  if (patientId) {
    const withPatient = linked.find((enc) => encounterMatchesPatient(enc, patientId));
    if (withPatient) return withPatient;
  }
  return linked[0];
}

/**
 * Resolve the Encounter for an appointment. Does not filter by encounter status — a
 * `planned` encounter after session end is still valid. Session-ended is separate UI state.
 */
export async function findEncounterForAppointment(
  medplum: ReturnType<typeof useMedplum>,
  appointmentId: string,
  patientId: string | undefined,
  appointmentDate: string | undefined,
): Promise<Encounter | undefined> {
  medplum.invalidateSearches('Encounter');

  const appointmentRefCandidates = [`Appointment/${appointmentId}`, appointmentId];

  for (const appointmentRef of appointmentRefCandidates) {
    try {
      const encounters = await medplum.searchResources('Encounter', {
        appointment: appointmentRef,
        _count: '20',
      });
      const match = pickEncounterForAppointment(encounters, appointmentId, patientId);
      if (match) return match;
    } catch (err) {
      console.warn('[findEncounterForAppointment] appointment search failed', {
        appointmentId,
        appointmentRef,
        error: err,
      });
    }
  }

  if (patientId && appointmentDate) {
    const dateOnly = appointmentDate.split('T')[0];
    try {
      const encounters = await medplum.searchResources('Encounter', {
        subject: `Patient/${patientId}`,
        date: dateOnly,
        _count: '20',
        _sort: '-date',
      });
      const match = pickEncounterForAppointment(encounters, appointmentId, patientId);
      if (match) return match;
    } catch (err) {
      console.warn('[findEncounterForAppointment] subject+date fallback failed', {
        appointmentId,
        patientId,
        appointmentDate: dateOnly,
        error: err,
      });
    }
  }

  console.warn('[findEncounterForAppointment] no Encounter found', {
    appointmentId,
    patientId,
    appointmentDate,
  });
  return undefined;
}

export async function findLinkedAppointments(
  medplum: ReturnType<typeof useMedplum>,
  patientId: string,
  currentAppointmentId: string,
  currentDate: string | undefined,
): Promise<{ previous?: LinkedAppointmentInfo; next?: LinkedAppointmentInfo }> {
  const result: { previous?: LinkedAppointmentInfo; next?: LinkedAppointmentInfo } = {};

  if (!currentDate) return result;

  try {
    const prevResults = await medplum.searchResources('Appointment', {
      patient: `Patient/${patientId}`,
      date: `lt${currentDate}`,
      _count: '1',
      _sort: '-date',
    });
    const prev = prevResults.find((a) => a.id !== currentAppointmentId);
    if (prev) {
      result.previous = {
        id: prev.id,
        date: formatLinkedDate(prev.start),
        time: formatLinkedTime(prev.start, prev.end),
        status: prev.status,
      };
    }
  } catch {
    // Silently fail
  }

  try {
    const nextResults = await medplum.searchResources('Appointment', {
      patient: `Patient/${patientId}`,
      date: `gt${currentDate}`,
      _count: '1',
      _sort: 'date',
    });
    const next = nextResults.find((a) => a.id !== currentAppointmentId);
    if (next) {
      result.next = {
        id: next.id,
        date: formatLinkedDate(next.start),
        time: formatLinkedTime(next.start, next.end),
        status: next.status,
      };
    }
  } catch {
    // Silently fail
  }

  return result;
}
